import {AutocompleteTextfield} from "../../../../@bizkeytech";
import {useFieldControl} from "../control";
import React, {useCallback, useMemo} from "react";

export type FieldAutocompleteValue<Value, Multiple> = Multiple extends true
    ? Value extends Array<infer Inner> ? Inner : never
    : Value;

export interface FieldAutocompleteProps<
    Value,
    Multiple extends boolean | undefined,
    FetchOptions extends boolean,
> {
    limitTags?: number;
    multiple?: Multiple;
    autoCommit?: boolean;
    getOptionLabel: (value: FieldAutocompleteValue<Value, Multiple>) => string;
    getOptionKey: (value: FieldAutocompleteValue<Value, Multiple>) => string | number;
    options?: FetchOptions extends false
        ? Array<FieldAutocompleteValue<Value, Multiple>>
        : never;
    getOptions?: FetchOptions extends true
        ? (keyword: string | undefined, abortController: AbortController) => Promise<Array<FieldAutocompleteValue<Value, Multiple>> | undefined>
        : never;
}

const FieldAutocomplete = <
    Value,
    Multiple extends boolean = false,
    FetchOptions extends boolean = false,
>(props: FieldAutocompleteProps<Value, Multiple, FetchOptions>) => {
    const {getOptionLabel: getOptionLabelProps, getOptionKey: getOptionKeyProps, getOptions: getOptionsProps} = props;

    const fieldControl = useFieldControl<FieldAutocompleteValue<Value, Multiple>>();
    const {setValue, saveChanges} = fieldControl;

    const limitTags = useMemo(() => props.limitTags ?? 2, [props.limitTags]);

    const value = useMemo<any>(() => {
        if (props.multiple) {
            return (fieldControl.value ?? []) as Array<FieldAutocompleteValue<Value, Multiple>>;
        }
        if (fieldControl.value) {
            return fieldControl.value;
        }
        return undefined as unknown as any;
    }, [props.multiple, fieldControl.value])

    /**
     * Sets the value of the field.
     */
    const onChange = useCallback(async (_: unknown, value: any) => {
        if (props.multiple) {
            const valueList = value as Array<FieldAutocompleteValue<Value, Multiple>> ?? [];
            setValue(valueList as unknown as FieldAutocompleteValue<Value, Multiple>);
        } else {
            setValue(value as FieldAutocompleteValue<Value, Multiple>);
        }
        if (props.autoCommit ?? true) {
            await saveChanges();
        }
    }, [props.multiple, setValue, props.autoCommit, saveChanges]);

    const options = useMemo<Array<Value>>(() => (props.options ?? []) as Value[], [props.options])

    /**
     * Fetches the options for the autocomplete field.
     */
    const getOptions = useCallback(async (keyword: string | undefined, abortController: AbortController): Promise<Array<Value> | undefined> => {
        if (!getOptionsProps) {
            return Promise.resolve(undefined);
        }
        return (getOptionsProps(keyword, abortController) as Promise<Array<Value> | undefined>);
    }, [getOptionsProps]);

    /**
     * Fetches the label of the option.
     */
    const getOptionLabel = useCallback((value: Value) => getOptionLabelProps(value as FieldAutocompleteValue<Value, Multiple>), [getOptionLabelProps]);

    /**
     * Fetches the key of the option.
     */
    const getOptionKey = useCallback((value: Value) => getOptionKeyProps(value as FieldAutocompleteValue<Value, Multiple>), [getOptionKeyProps]);

    return (
        <>
            <AutocompleteTextfield<Value, "Mui", FetchOptions, Multiple>
                form={false}
                size={'small'}
                fullWidth
                value={value}
                onChange={onChange}
                disabled={fieldControl.disabled}
                readOnly={fieldControl.readonly}
                multiple={props.multiple}
                limitTags={limitTags}
                getOptionLabel={getOptionLabel}
                getOptionKey={getOptionKey}
                // Any is used here because the FetchOptions is just a type validation and not a runtime value.
                getOptions={getOptions as any}
                options={options as any}
            />
        </>
    );
}

export default FieldAutocomplete;
